import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import { APP_NAME } from './brand';
import type { AppData } from './crawl/engine';
import { clearCrawlParams, readCrawlParams, type CrawlLink } from './crawl/link';
import type { Crawl, CrawlStart, LatLon } from './crawl/plan';
import type { WalkingRoute } from './crawl/route';
import { hasSavedCrawl } from './crawl/saved';
import { useCrawl } from './crawl/useCrawl';
import { LocateButton } from './components/LocateButton';
import { SearchBox } from './components/SearchBox';
import type { PlaceResult } from './data/postcodes';
import {
  emptyStore,
  findPub,
  loadDetails,
  loadMeta,
  loadOverview,
  loadSearch,
  overviewGeoJSON,
  shardOf,
  shardsFor,
  summary,
  withShards,
  type PubHit,
  type PubStore,
} from './data/pubs';
import type { DatasetMeta, OverviewFile, PubDetails } from './data/schema';
import { activeCount, isFiltered, readFilters, type Filters } from './lib/filters';
import { readPubParam, readTimeParam, writeFiltersParam, writePubParam } from './lib/urlState';
import type { MapViewHandle, PointerKind } from './map/MapView';
import { useLeaderboard, type LeaderboardMode } from './map/useLeaderboard';
import { useScorePins } from './map/useScorePins';

/** Shards load from just below the zoom where pub points appear (SHARD_MIN_ZOOM = 9). */
const SHARDS_FROM_ZOOM = 8.5;

// Only the shell (header, search, buttons) is in the first bundle. MapLibre
// (~1 MB) and everything shown only after a tap are separate chunks.
const MapView = lazy(() => import('./map/MapView').then((m) => ({ default: m.MapView })));
const PubPanel = lazy(() => import('./components/PubPanel').then((m) => ({ default: m.PubPanel })));
const PreviewCard = lazy(() =>
  import('./components/PreviewCard').then((m) => ({ default: m.PreviewCard })),
);
const Leaderboard = lazy(() =>
  import('./components/Leaderboard').then((m) => ({ default: m.Leaderboard })),
);
const FilterPanel = lazy(() =>
  import('./components/FilterPanel').then((m) => ({ default: m.FilterPanel })),
);
const CrawlPanel = lazy(() =>
  import('./components/CrawlPanel').then((m) => ({ default: m.CrawlPanel })),
);

/** The static boot screen in index.html: fade it out once the map is up. */
function hideBoot() {
  const boot = document.getElementById('boot');
  if (!boot || boot.classList.contains('boot--done')) return;
  boot.classList.add('boot--done');
  window.setTimeout(() => boot.remove(), 400);
}

/**
 * Room for the pub panel (420px, left) and the leaderboard or crawl column
 * (~400px, right) side by side with the bottom bar between them. Narrower
 * than this, one side panel at a time (as on phones).
 */
const TWO_PANELS = '(min-width: 1240px)';
const roomForTwoPanels = () => window.matchMedia(TWO_PANELS).matches;

type Preview = { index: number; mode: 'hover' | 'tap'; at: { x: number; y: number } | null };

/** Where a crawl's walk goes, in order: the start (if any), then each pub. */
const crawlPath = (crawl: Crawl): LatLon[] => [
  ...(crawl.start ? [crawl.start] : []),
  ...crawl.stops.map((s) => s.pub),
];

/**
 * The crawl on the map: the walking route (along real streets once it's
 * fetched, straight lines until then or if routing fails), the start, and
 * numbered stops.
 */
function crawlGeoJSON(
  crawl: Crawl | null,
  start: CrawlStart | null,
  route: WalkingRoute | null,
): FeatureCollection | null {
  const from = crawl ? crawl.start : start;
  const stops = crawl?.stops ?? [];
  if (!from && stops.length === 0) return null;
  const path = [...(from ? [from] : []), ...stops.map((s) => s.pub)];
  return {
    type: 'FeatureCollection',
    features: [
      ...(path.length > 1
        ? [
            {
              type: 'Feature' as const,
              properties: { kind: 'route', routed: route !== null },
              geometry: {
                type: 'LineString' as const,
                coordinates: route ? route.coordinates : path.map((p) => [p.lon, p.lat]),
              },
            },
          ]
        : []),
      ...(from
        ? [
            {
              type: 'Feature' as const,
              properties: { kind: 'start' },
              geometry: { type: 'Point' as const, coordinates: [from.lon, from.lat] },
            },
          ]
        : []),
      ...stops.map((s, n) => ({
        type: 'Feature' as const,
        properties: { kind: 'stop', n: n + 1, i: s.pub.index },
        geometry: { type: 'Point' as const, coordinates: [s.pub.lon, s.pub.lat] },
      })),
    ],
  };
}

export function App() {
  const mapRef = useRef<MapViewHandle>(null);
  const [store, setStore] = useState<PubStore | null>(null);
  const [meta, setMeta] = useState<DatasetMeta | null>(null);
  const [overview, setOverview] = useState<OverviewFile | null>(null);
  /** The latest store, for merging in shards as they arrive. */
  const storeRef = useRef<PubStore | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  /** A shared crawl link (?crawl=…), opened once the pub data is ready. */
  const [sharedCrawl] = useState<CrawlLink | null>(() => readCrawlParams(window.location.search));
  const sharedCrawlOpened = useRef(false);
  /** A shared link's hour (?t=), for the pub it opened. */
  const [sharedTime, setSharedTime] = useState<number | null>(() =>
    sharedCrawl ? null : readTimeParam(),
  );
  const [details, setDetails] = useState<PubDetails | null>(null);
  const [detailsError, setDetailsError] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [me, setMe] = useState<{ lat: number; lon: number } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [pinsOn, setPinsOn] = useState(true);
  const [filters, setFiltersState] = useState<Filters>(() => readFilters(window.location.search));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardMode, setBoardMode] = useState<LeaderboardMode>('now');
  // A shared link, or a crawl you're part-way through, opens the planner.
  const [crawlOpen, setCrawlOpen] = useState(() => sharedCrawl !== null || hasSavedCrawl());
  /** Waiting for a tap on the map to set the crawl's start. */
  const [pickingStart, setPickingStart] = useState(false);
  const [locating, setLocating] = useState(false);
  /** A pub opened from the crawl on a phone: closing it brings the crawl back. */
  const backToCrawl = useRef(false);
  const crawlOpenRef = useRef(crawlOpen);
  useEffect(() => {
    crawlOpenRef.current = crawlOpen;
  }, [crawlOpen]);
  const filtersButton = useRef<HTMLButtonElement>(null);
  const boardButton = useRef<HTMLButtonElement>(null);
  const crawlButton = useRef<HTMLButtonElement>(null);
  // Mount the (lazily loaded) map once the page has painted and the browser
  // is idle, so the header, search and buttons show up before the 1 MB map
  // library starts downloading. On a real phone that's a fraction of a second.
  const [showMap, setShowMap] = useState(false);
  useEffect(() => {
    const show = () => setShowMap(true);
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(show, { timeout: 800 });
      return () => cancelIdleCallback(id);
    }
    const timer = window.setTimeout(show, 300);
    return () => window.clearTimeout(timer);
  }, []);
  const scorePins = useScorePins(mapRef, store, pinsOn);
  const board = useLeaderboard(mapRef, store, boardOpen, boardMode);

  const setFilters = useCallback((next: Filters) => {
    setFiltersState(next);
    writeFiltersParam(next);
  }, []);

  // New filters change which pubs are in view: refresh pins and the board.
  const { onMoveEnd: pinsMoveEnd } = scorePins;
  const { refresh: refreshBoard } = board;
  useEffect(() => {
    pinsMoveEnd();
    refreshBoard();
  }, [filters, store, pinsMoveEnd, refreshBoard]);

  /** Add shards to the store (in order, so concurrent loads don't drop pubs). */
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const addShards = useCallback((keys: string[]): Promise<PubStore | null> => {
    const run = queue.current.then(async () => {
      const current = storeRef.current;
      if (!current) return null;
      const next = await withShards(current, keys);
      if (next !== current) {
        storeRef.current = next;
        setStore(next);
      }
      return next;
    });
    queue.current = run.catch(() => undefined);
    return run;
  }, []);

  // What the crawl planner needs from the app: pubs in an area, pubs by id.
  const crawlData = useMemo<AppData | null>(
    () =>
      meta
        ? {
            loadBox: async (box) => {
              const next = await addShards(shardsFor(meta, box, 0));
              if (!next) throw new Error('Pub data not loaded');
              return next;
            },
            findPubs: async (ids) => {
              const search = await loadSearch();
              return ids.map((id) => findPub(search, id));
            },
          }
        : null,
    [meta, addShards],
  );
  const crawl = useCrawl(crawlData, filters);
  const {
    openShared,
    restore: restoreCrawl,
    hadSaved: hadSavedCrawl,
    setForm: setCrawlForm,
    edit: editCrawl,
    end: endCrawl,
  } = crawl;
  // Open a shared link, or bring back the crawl you were on, once the data's ready.
  useEffect(() => {
    if (!crawlData || sharedCrawlOpened.current) return;
    if (sharedCrawl) openShared(sharedCrawl);
    else if (hadSavedCrawl) restoreCrawl();
    else return;
    sharedCrawlOpened.current = true;
  }, [sharedCrawl, crawlData, openShared, hadSavedCrawl, restoreCrawl]);

  /** Load the shards for the map view (from town zoom; below it the overview shows). */
  const loadView = useCallback(() => {
    const view = mapRef.current?.viewport();
    if (!view || !meta || view.zoom < SHARDS_FROM_ZOOM) return;
    addShards(shardsFor(meta, view)).catch(() =>
      setToast('Couldn’t load the pubs here. Check your connection and move the map to retry.'),
    );
  }, [meta, addShards]);

  // The map mounts lazily, so a camera move asked for before it's ready (a
  // deep link, an early search) waits here and runs when it loads.
  const mapReady = useRef(false);
  const pendingMove = useRef<((map: MapViewHandle) => void) | null>(null);
  const moveMap = useCallback((move: (map: MapViewHandle) => void) => {
    if (mapReady.current && mapRef.current) move(mapRef.current);
    else pendingMove.current = move;
  }, []);
  const onMapReady = useCallback(() => {
    mapReady.current = true;
    hideBoot();
    const move = pendingMove.current;
    pendingMove.current = null;
    if (move && mapRef.current) move(mapRef.current);
    loadView();
  }, [loadView]);

  const openPubIn = useCallback(
    (s: PubStore, index: number, options: { fly?: boolean } = {}) => {
      setPreview(null);
      if (!roomForTwoPanels()) {
        // The pub takes the place of the board or crawl; the crawl comes back when it closes.
        if (crawlOpenRef.current) backToCrawl.current = true;
        setCrawlOpen(false);
        setBoardOpen(false);
      }
      setSelected(index);
      setDetails(null);
      setDetailsError(false);
      writePubParam(s.ids[index] ?? null);
      const target = {
        lat: s.lat[index] ?? 0,
        lon: s.lon[index] ?? 0,
        ...(options.fly ? { zoom: 16 } : {}),
      };
      moveMap((map) => map.reveal(target));
      loadDetails(s, index)
        .then((d) => setDetails(d))
        .catch(() => setDetailsError(true));
    },
    [moveMap],
  );

  const openPub = useCallback(
    (index: number, options: { fly?: boolean } = {}) => {
      setSharedTime(null);
      if (store) openPubIn(store, index, options);
    },
    [store, openPubIn],
  );

  /** Open a pub found by search or a shared link: load its shard first. */
  const openHit = useCallback(
    (hit: PubHit, options: { fly?: boolean; keepTime?: boolean } = {}) => {
      if (!options.keepTime) setSharedTime(null);
      addShards([shardOf(hit.lat, hit.lon)])
        .then((s) => {
          const index = s?.indexOf.get(hit.id);
          if (s && index !== undefined) openPubIn(s, index, options);
        })
        .catch(() => setToast('Couldn’t load that pub. Check your connection and try again.'));
    },
    [addShards, openPubIn],
  );

  const load = useCallback(() => {
    Promise.all([loadMeta(), loadOverview()])
      .then(([m, o]) => {
        const s = emptyStore(m);
        storeRef.current = s;
        setMeta(m);
        setOverview(o);
        setStore(s);
        // Deep link: ?pub=w123 finds the pub in the search file, then opens it.
        const id = readPubParam();
        if (id) {
          loadSearch()
            .then((search) => {
              const hit = findPub(search, id);
              if (hit) openHit(hit, { fly: true, keepTime: true });
            })
            .catch(() => setToast('Couldn’t find that pub. The link may be out of date.'));
        }
      })
      .catch((error: unknown) => {
        hideBoot();
        setLoadError(error instanceof Error ? error.message : 'Pub data failed to load');
      });
  }, [openHit]);
  useEffect(load, [load]);

  // If the map is slow (or WebGL is missing), don't leave the boot screen up.
  useEffect(() => {
    const timer = window.setTimeout(hideBoot, 6000);
    return () => window.clearTimeout(timer);
  }, []);

  const overviewData = useMemo(
    () => (overview ? overviewGeoJSON(overview, filters) : null),
    [overview, filters],
  );

  // Toasts fade after a few seconds.
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  /** Close the pub panel to make room for the board or crawl (no crawl to come back to). */
  const dropPub = useCallback(() => {
    backToCrawl.current = false;
    setSelected(null);
    setSharedTime(null);
    writePubParam(null);
  }, []);

  const closePub = useCallback(() => {
    setSelected(null);
    setSharedTime(null);
    writePubParam(null);
    if (backToCrawl.current) {
      backToCrawl.current = false;
      setCrawlOpen(true);
    }
  }, []);

  const onHover = useCallback(
    (index: number | null, at: { x: number; y: number } | null) => {
      setPreview((current) => {
        if (index === null) return current?.mode === 'hover' ? null : current;
        if (index === selected) return null;
        return { index, mode: 'hover', at };
      });
    },
    [selected],
  );

  const setCrawlStart = useCallback(
    (start: CrawlStart) => setCrawlForm((form) => ({ ...form, start })),
    [setCrawlForm],
  );

  const onPick = useCallback(
    (index: number | null, pointer: PointerKind, point?: { x: number; y: number }) => {
      if (pickingStart) {
        setPickingStart(false);
        setPreview(null);
        const at =
          index !== null && store
            ? { lat: store.lat[index] ?? 0, lon: store.lon[index] ?? 0 }
            : point
              ? mapRef.current?.unproject(point)
              : null;
        if (at) {
          setCrawlStart({
            ...at,
            label: index !== null && store ? (store.names[index] ?? 'Dropped pin') : 'Dropped pin',
          });
        }
        return;
      }
      if (index === null) {
        setPreview(null);
        return;
      }
      // Mouse: straight to details. Touch: first tap previews, unless details are already open.
      if (pointer === 'mouse' || selected !== null) {
        openPub(index);
        return;
      }
      setPreview((current) =>
        current?.mode === 'tap' && current.index === index
          ? current
          : { index, mode: 'tap', at: null },
      );
    },
    [openPub, selected, pickingStart, store, setCrawlStart],
  );

  /** A crawl stop (on the map or in the list): open its pub. */
  const onStop = useCallback(
    (index: number) => {
      if (pickingStart) return;
      openPub(index, { fly: true });
    },
    [openPub, pickingStart],
  );

  const crawlState = crawl.state;
  const currentCrawl = crawl.current;
  // While planning a replacement, the map shows the new start; the crawl comes back after.
  const shownCrawl = crawlOpen && crawl.editing ? null : (currentCrawl?.crawl ?? null);
  // The walking route along real streets, fetched (and cached) for each crawl shown.
  const crawlPoints = useMemo(() => (shownCrawl ? crawlPath(shownCrawl) : null), [shownCrawl]);
  const [walk, setWalk] = useState<{ points: LatLon[]; route: WalkingRoute } | null>(null);
  useEffect(() => {
    if (!crawlPoints || crawlPoints.length < 2) return;
    let live = true;
    import('./crawl/route')
      .then(({ walkingRoute }) => walkingRoute(crawlPoints))
      .then((route) => {
        if (live) setWalk({ points: crawlPoints, route });
      })
      .catch(() => {
        // Straight lines it is.
      });
    return () => {
      live = false;
    };
  }, [crawlPoints]);
  const walkRoute = walk && walk.points === crawlPoints ? walk.route : null;
  const crawlMap = useMemo(
    () => crawlGeoJSON(shownCrawl, crawl.form.start, walkRoute),
    [shownCrawl, crawl.form.start, walkRoute],
  );
  // Show a newly chosen start (above the crawl sheet on phones).
  const crawlStart = crawl.form.start;
  useEffect(() => {
    if (crawlStart)
      moveMap((map) => map.reveal({ lat: crawlStart.lat, lon: crawlStart.lon, zoom: 14 }));
  }, [crawlStart, moveMap]);

  // Fit a new crawl on the map.
  useEffect(() => {
    if (!shownCrawl) return;
    const points = [
      ...(shownCrawl.start ? [shownCrawl.start] : []),
      ...shownCrawl.stops.map((s) => s.pub),
    ];
    moveMap((map) => map.fitPoints(points));
  }, [shownCrawl, moveMap]);

  const closeCrawl = useCallback(() => {
    setCrawlOpen(false);
    setPickingStart(false);
    crawlButton.current?.focus();
  }, []);
  // A shared link stays in the address bar only while it's the crawl you're on.
  const dropCrawlLink = useCallback(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('crawl')) window.history.replaceState(null, '', clearCrawlParams(url));
  }, []);
  useEffect(() => {
    if (currentCrawl && !currentCrawl.shared) dropCrawlLink();
  }, [currentCrawl, dropCrawlLink]);
  const finishCrawl = useCallback(() => {
    endCrawl();
    dropCrawlLink();
  }, [endCrawl, dropCrawlLink]);
  const locateForCrawl = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setToast('Your browser can’t share your location. Search a postcode or pick on the map.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        const at = { lat: position.coords.latitude, lon: position.coords.longitude };
        setMe(at);
        setCrawlStart({ ...at, label: 'Your location' });
        moveMap((map) => map.flyTo({ ...at, zoom: 15 }));
      },
      (error) => {
        setLocating(false);
        setToast(
          error.code === error.PERMISSION_DENIED
            ? 'Location is switched off for this site. Search a postcode or pick on the map instead.'
            : 'Couldn’t find you just now. Try again, or search a postcode.',
        );
      },
      { enableHighAccuracy: false, timeout: 12_000, maximumAge: 60_000 },
    );
  }, [moveMap, setCrawlStart]);

  const onPlace = useCallback(
    (place: PlaceResult) => {
      setPreview(null);
      moveMap((map) => map.flyTo({ lat: place.lat, lon: place.lon, zoom: place.zoom }));
    },
    [moveMap],
  );

  const closeBoard = useCallback(() => {
    setBoardOpen(false);
    boardButton.current?.focus();
  }, []);
  const closeFilters = useCallback(() => {
    setFiltersOpen(false);
    filtersButton.current?.focus();
  }, []);

  const centre = useCallback(() => me ?? undefined, [me]);
  const selectedPub = store && selected !== null ? summary(store, selected) : null;
  const previewPub = store && preview ? summary(store, preview.index) : null;
  const filterCount = activeCount(filters);

  return (
    <div
      className={`app${selectedPub ? ' app--panel-open' : ''}${boardOpen ? ' app--board-open' : ''}${crawlOpen ? ' app--crawl-open' : ''}`}
    >
      <header className="topbar">
        <h1 className="brand">
          <span className="brand__mark" aria-hidden="true">
            <img src="/favicon.svg" alt="" width="32" height="32" />
          </span>
          <span className="brand__text">{APP_NAME}</span>
        </h1>
        <SearchBox near={centre} onPlace={onPlace} onPub={(hit) => openHit(hit, { fly: true })} />
        <LocateButton
          onLocated={(position) => {
            setMe(position);
            moveMap((map) => map.flyTo({ ...position, zoom: 15 }));
          }}
          onError={setToast}
        />
        <a className="icon-button topbar__about" href="/about" aria-label={`About ${APP_NAME}`}>
          ?
        </a>
      </header>

      <main className="stage">
        <Suspense fallback={<div className="map map--loading" />}>
          {showMap && (
            <MapView
              ref={mapRef}
              store={store}
              filters={filters}
              selected={selected ?? (preview?.mode === 'tap' ? preview.index : null)}
              me={me}
              scorePins={scorePins.pins}
              overview={overviewData}
              crawl={crawlMap}
              onHover={onHover}
              onPick={onPick}
              onStop={onStop}
              onReady={onMapReady}
              onMoveEnd={() => {
                loadView();
                board.onMoveEnd();
                scorePins.onMoveEnd();
              }}
            />
          )}
        </Suspense>

        {!store && !loadError && (
          <p className="status-pill" role="status">
            Pouring 38,000 pubs onto the map…
          </p>
        )}
        {loadError && (
          <div className="status-pill status-pill--error" role="alert">
            {loadError}.{' '}
            <button
              type="button"
              className="text-button"
              onClick={() => {
                setLoadError(null);
                load();
              }}
            >
              Try again
            </button>
          </div>
        )}

        <div className="bottombar">
          {filtersOpen && (
            <Suspense fallback={null}>
              <FilterPanel filters={filters} onChange={setFilters} onClose={closeFilters} />
            </Suspense>
          )}
          <div className="bottombar__row">
            <button
              ref={boardButton}
              type="button"
              className="pill-button"
              disabled={!store}
              aria-pressed={boardOpen}
              onClick={() => {
                setCrawlOpen(false);
                if (!boardOpen && !roomForTwoPanels()) dropPub();
                setBoardOpen((open) => !open);
              }}
            >
              <span aria-hidden="true">🏆</span> Leaderboard
            </button>
            <button
              ref={crawlButton}
              type="button"
              className="pill-button"
              disabled={!store}
              aria-pressed={crawlOpen}
              onClick={() => {
                setBoardOpen(false);
                if (!crawlOpen && !roomForTwoPanels()) dropPub();
                setCrawlOpen((open) => !open);
              }}
            >
              <span aria-hidden="true">🍻</span> Pub crawl
              {currentCrawl && !crawlOpen && (
                <span className="pill-button__live">
                  <span className="visually-hidden"> (you have a crawl on)</span>
                </span>
              )}
            </button>
            <button
              ref={filtersButton}
              type="button"
              className="pill-button"
              disabled={!store}
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              <span aria-hidden="true">⚙︎</span> Filters
              {filterCount > 0 && (
                <span className="pill-button__count">
                  {filterCount}
                  <span className="visually-hidden"> on</span>
                </span>
              )}
            </button>
            <button
              type="button"
              className="pill-button"
              disabled={!store}
              aria-pressed={pinsOn}
              onClick={() => setPinsOn((on) => !on)}
            >
              <span aria-hidden="true">🍺</span> Scores on pins
            </button>
          </div>
          {pinsOn && (
            // Always rendered while pins are on (so it paints with the page), with
            // the zoom hint until the map says otherwise.
            <p className="pins-hint" role="status">
              {scorePins.hint ?? 'Zoom in closer to see scores on the pins.'}
            </p>
          )}
          <p className="legend">
            <span className="legend__item">
              <span className="legend__dot legend__dot--garden" aria-hidden="true" /> Beer garden or
              outdoor seating confirmed
            </span>
            {!filters.confirmed && (
              <span className="legend__item">
                <span className="legend__dot" aria-hidden="true" /> Outside space unknown
              </span>
            )}
            {isFiltered(filters) && <span className="legend__item">Filtered</span>}
          </p>
        </div>

        {boardOpen && (
          <Suspense fallback={null}>
            <Leaderboard
              state={board.state}
              mode={boardMode}
              onMode={setBoardMode}
              onPick={(index) => openPub(index, { fly: true })}
              onClose={closeBoard}
            />
          </Suspense>
        )}

        {crawlOpen && (
          <Suspense fallback={null}>
            <CrawlPanel
              form={crawl.form}
              onForm={crawl.setForm}
              state={crawlState}
              picking={pickingStart}
              onPickOnMap={() => setPickingStart((on) => !on)}
              onUseMyLocation={locateForCrawl}
              locating={locating}
              onPlan={crawl.plan}
              current={currentCrawl}
              editing={crawl.editing}
              onEdit={editCrawl}
              onResume={crawl.resume}
              onEnd={finishCrawl}
              onRetry={crawl.retry}
              onStop={onStop}
              onClose={closeCrawl}
            />
          </Suspense>
        )}

        {pickingStart && (
          <p className="pick-hint" role="status">
            Tap the map where you’re starting.{' '}
            <button type="button" className="text-button" onClick={() => setPickingStart(false)}>
              Cancel
            </button>
          </p>
        )}

        {previewPub && preview && (
          <Suspense fallback={null}>
            <PreviewCard
              pub={previewPub}
              mode={preview.mode}
              at={preview.at}
              onOpen={() => openPub(preview.index)}
              onDismiss={() => setPreview(null)}
            />
          </Suspense>
        )}

        {selectedPub && (
          <Suspense fallback={null}>
            <PubPanel
              key={selectedPub.id}
              pub={selectedPub}
              details={details}
              detailsError={detailsError}
              initialTime={sharedTime}
              onClose={closePub}
            />
          </Suspense>
        )}

        {toast && (
          <p className="toast" role="alert">
            {toast}
          </p>
        )}
      </main>
    </div>
  );
}
